<section class="main">
  <!-- Header -->
  <ts-page-header>
    <ng-container page-header-title>
      <section class="list__top__title-wrapper">
        <h5 class="list__top__title" data-cy="properties-saved-view-title">{{ savedView()?.name }}</h5>
      </section>
    </ng-container>
    <ng-container page-header-action-buttons>
      <section class="list__top__action-buttons">
        <ts-button
          color="accent"
          type="stroked"
          icon="ts_file-save"
          [appAuthorize]="NewPermission.PROPERTIES_SHARING_RECORDS"
          denyAction="disable"
          [matTooltip]="properties?.length ? 'Export as CSV' : 'No data to export'"
          [disabled]="!properties?.length || exportingCsv"
          (click)="exportPropertiesAsCSV()"
          [loading]="exportingCsv"
        >
          Export
        </ts-button>
        <ts-button
          [appAuthorize]="getCreatePropertyPermission()"
          data-cy="create-new-property"
          color="primary"
          icon="add"
          [matTooltip]="'Add new property'"
          (click)="createProperty()"
          denyAction="disable"
        >
          Add
        </ts-button>
      </section>
    </ng-container>
    <ng-container page-header-action-bar>
      <section class="list__bottom__action-buttons justify-between w-full">
        <div class="flex flex-row items-center gap-2">
          <!-- Search Bar -->
          <ts-input
            [formControl]="searchInputControl"
            [clearable]="true"
            size="sm"
            type="text"
            icon="ts_search"
            placeholder="Search"
          ></ts-input>

          <!-- Column Select Panel Button -->
          <ts-button
            type="stroked"
            color="accent"
            class="xs:hidden"
            icon="visibility"
            [matTooltip]="'Select columns'"
            [tsOverlayTriggerFor]="columnsSelectOverlayBox"
          >
            Fields
            <ng-container *ngIf="displayColumns.length">
              <ts-badge [value]="displayColumns.length" [type]="'info'"></ts-badge>
            </ng-container>
          </ts-button>

          <!-- List Filter Button -->
          <ts-button
            type="stroked"
            color="accent"
            class="xs:hidden"
            [tsOverlayTriggerFor]="filtersOverlayBox"
            [matTooltip]="'Apply filters'"
          >
            <ts-icon icon="ts_filter-list" class="mr-2"></ts-icon>
            Filter
            <ng-container *ngIf="filterCounter">
              <ts-badge [value]="filterCounter" [type]="'info'"></ts-badge>
            </ng-container>
          </ts-button>
        </div>

        @if (NewPermission.PROPERTIES_PUBLIC_CUSTOM_VIEWS | hasPermission) {
          <ts-menu [items]="viewManagementOptions()" />
        }
      </section>
    </ng-container>
  </ts-page-header>

  <!-- Loading Bar -->
  <ng-container *ngIf="isLoading()">
    <mat-progress-bar [mode]="'indeterminate'"></mat-progress-bar>
  </ng-container>

  <!-- Properties Table -->
  <main class="properties-list" [ngClass]="{ 'search-loading': isLoading() }">
    <div class="table-wrapper">
      <!-- TsTable -->
      @if (newTable) {
        <ts-table
          [relatedToRedirectConfig]="relatedToRedirectConfig"
          [columnDefs]="columnDefs"
          [rowData]="properties"
          [rowSelection]="'multiple'"
          [data]="routeData"
          [hasCheckboxColumn]="true"
          [hasOpenDetailsColumn]="true"
          [autoHeight]="false"
          (selectionChanged)="onSelectionChanged($event)"
          (cellEditingStopped)="onCellEditingStopped($event)"
          (onColumnMove)="onColumnMoved($event)"
          (sortChanged)="onSortChanged($event)"
          (openDetails)="goToProperty($event.id)"
          (gridReady)="gridReady($event)"
        >
        </ts-table>
      } @else {
        <table
          mat-table
          matSort
          [matSortActive]="sortState.active"
          [matSortDisableClear]="true"
          [matSortDirection]="sortState.direction"
          [dataSource]="properties"
          [trackBy]="trackByFn"
          class="table-container"
          cdkDropList
          cdkDropListOrientation="horizontal"
          (cdkDropListDropped)="dropColumns($event)"
          data-cy="properties-list"
          (matSortChange)="sortChanged($event)"
        >
          <!-- Checkbox -->
          <ng-container matColumnDef="checkbox" sticky>
            <th mat-header-cell *matHeaderCellDef class="checkbox-header">
              <mat-checkbox
                [color]="'primary'"
                (change)="$event ? masterToggle() : null"
                [checked]="selection.hasValue() && isAllSelected()"
                [indeterminate]="selection.hasValue() && !isAllSelected()"
                data-cy="select-all-property-checkbox"
              ></mat-checkbox>
            </th>
            <td mat-cell *matCellDef="let property" class="checkbox-cell">
              <mat-checkbox
                [color]="'primary'"
                (click)="$event.stopPropagation()"
                (change)="$event ? selection.toggle(property) : null"
                [checked]="selection.isSelected(property)"
                tabindex="-1"
              ></mat-checkbox>
            </td>
          </ng-container>

          <!-- Name -->
          <ng-container matColumnDef="name" sticky>
            <th mat-header-cell mat-sort-header *matHeaderCellDef class="title-header">{{ titleField.label }}</th>
            <td mat-cell *matCellDef="let property" class="title-cell">
              <div class="title-content pl-2">
                <span class="title font-medium">
                  <a data-cy="property-name-label" [routerLink]="['../../' + property.id]" class="underline">
                    {{ property?.name || property?.address_string || 'Untitled' }}</a
                  >
                </span>
              </div>
            </td>
          </ng-container>

          <ng-container [matColumnDef]="column" *ngFor="let column of displayColumns; index as i">
            <ng-container *ngIf="lookupField(column) as fieldDef">
              <th
                [resizeColumn]="true"
                [index]="i"
                mat-header-cell
                mat-sort-header
                [disabled]="!fieldDef.meta || !fieldDef.meta.sorting || fieldDef.related_to_type !== 'Property'"
                *matHeaderCellDef
                cdkDrag
                cdkDragBoundary=".table-container"
                cdkDropList
                cdkDragLockAxis="x"
                [ngClass]="'project-table-head cw-' + i"
              >
                <div class="drag-handle" cdkDragHandle>
                  <mat-icon [svgIcon]="'drag_indicator'"></mat-icon>
                </div>
                <p>{{ fieldDef.groupLabel }} {{ fieldDef.label }}</p>
              </th>
              <td mat-cell *matCellDef="let property" contenteditable="!fieldDef.meta.readonly">
                <app-editable
                  [readonly]="fieldDef.related_to_type !== 'Property' || !property.permissions?.is_editable"
                  [fieldDef]="fieldDef"
                  [model]="property"
                  [data]="routeData"
                  (onUpdate)="onUpdate($event, property)"
                ></app-editable>
              </td>
            </ng-container>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="loopDisplayColumns; sticky: true"></tr>
          <tr class="property-row" mat-row *matRowDef="let property; columns: loopDisplayColumns"></tr>
        </table>

        <ng-container *ngIf="properties.length === 0 && !isLoading()">
          <section class="no-properties">There are no properties!</section>
        </ng-container>
      }
    </div>
  </main>

  <!-- Properties Table Footer -->
  <div [ngClass]="{ 'search-loading': isLoading() }">
    <!-- ExportCSV Footer Container -->
    <div class="flex justify-start items-center">
      <ng-container *ngIf="selection.selected.length > 0">
        <ng-container *ngIf="account && account.subdomain !== 'postal'">
          <ts-button
            [variant]="'primary'"
            [matMenuTriggerFor]="addDealMenuButton"
            data-cy="add-portfolio-button"
            [appAuthorize]="Permission.DEALS_ADD"
          >
            <mat-icon [svgIcon]="'add'"></mat-icon>
            Create Deal From {{ selection.selected.length }} Selected Properties
          </ts-button>

          <mat-menu #addDealMenuButton="matMenu">
            <button
              mat-menu-item
              (click)="createDealFromSelectedProperties('single')"
              data-cy="create-single-deal-option"
            >
              <mat-icon [svgIcon]="'add'"></mat-icon>
              <span>Create Single Deal</span>
            </button>
            <button
              mat-menu-item
              (click)="createDealFromSelectedProperties('portfolio')"
              *ngIf="selection.selected.length > 1"
              data-cy="create-portfolio-option"
            >
              <mat-icon [svgIcon]="'add'"></mat-icon>
              <span>Create Portfolio</span>
            </button>
          </mat-menu>
        </ng-container>

        <ng-container *ngIf="account && account.subdomain === 'postal'">
          <button mat-flat-button [color]="'primary'" (click)="createPostalDealFromSelectedProperties('portfolio')">
            <mat-icon [svgIcon]="'add'"></mat-icon>
            Create Deal From {{ selection.selected.length }} Selected Properties
          </button>
        </ng-container>
      </ng-container>
    </div>

    <!-- Right Side Footer Paginator -->
    @if (pageInfo) {
      <ts-paginator
        [pageSize]="pageSize"
        [activePage]="pageInfo.currentPage || 1"
        [totalItems]="pageInfo.filteredCount"
        (pageChanged)="pageChanged($event)"
      ></ts-paginator>
    }
  </div>
</section>

<!-- Column Select Panel -->
<ts-overlay #columnsSelectOverlayBox position="bottom" [transparentBackdrop]="true">
  <div class="properties-field-def-panel">
    <div class="fields-panel-header">
      <div class="new-field">
        <mat-icon [svgIcon]="'search'"></mat-icon>
        <input
          type="text"
          placeholder="Enter field name"
          (input)="filterFields($event)"
          (keydown)="filterFieldsInputKeyDown($event)"
          [maxLength]="50"
          #newTagInput
        />
      </div>
    </div>

    <div class="available-fields">
      <ng-container *ngIf="nestedFiltersEnabled; else noNestedFields">
        <ng-container
          *ngFor="let filteredField of filteredFieldDefs | groupBy: '_parentFieldName' : '_parentFieldLabel'"
        >
          <p *ngIf="filteredField?.groupLabel?.length > 1" class="font-bold text-gray-default p-2">
            {{ filteredField?.groupLabel }}
          </p>
          <ng-container *ngFor="let item of filteredField.items; trackBy: trackByFn">
            <mat-checkbox
              *ngIf="filteredField?.groupLabel?.length > 1 || item?.type !== 'record'"
              [ngClass]="{ 'pl-3': filteredField?.groupLabel?.length > 1 }"
              [disabled]="displayColumns.length < 2 && displayColumns.includes(item.name)"
              [color]="'primary'"
              [checked]="displayColumns.includes(item.name)"
              (change)="toggleDisplayColumn(item.name, $event)"
            >
              {{ item.label }}
            </mat-checkbox>
          </ng-container>
        </ng-container>
      </ng-container>

      <ng-template #noNestedFields>
        <mat-checkbox
          *ngFor="let fieldDef of filteredFieldDefs; trackBy: trackByFn"
          [disabled]="displayColumns.length < 2 && displayColumns.includes(fieldDef.name)"
          [color]="'primary'"
          [checked]="displayColumns.includes(fieldDef.name)"
          (change)="toggleDisplayColumn(fieldDef.name, $event)"
        >
          {{ fieldDef.label }}
        </mat-checkbox>
      </ng-template>
    </div>
  </div>
</ts-overlay>

<!-- List Filters Panel -->
<ts-overlay
  #filtersOverlayBox
  position="bottom"
  [transparentBackdrop]="true"
  (closeByBackdropClick)="onCloseByBackdropClick()"
>
  <ts-filter
    size="large"
    [fields]="typeFields | async"
    [filters]="filterValueList | async"
    [lookupContract]="lookupService"
    [hasGroups]="true"
    (addItem)="onAddFilterItem()"
    (filterDefChange)="onFilterDefChanged($event)"
    (filterRemove)="onFilterRemoved($event)"
    (filterClear)="onFilterCleared()"
    (filterApply)="onFilterApplied()"
  ></ts-filter>
</ts-overlay>
